import React, {useState, useEffect} from 'react';
import {Box, Grid, Typography, CircularProgress} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import '@fontsource/roboto';
import PartnerRevenueOptionsForm from './options/PartnerRevenueOptionsForm';
import DownloadButtonWithHaders from '../../../components/reports/DownloadButtonWithHaders';
import PartnerRevenueDataGrid from './components/PartnerRevenueDataGrid';
import {fetchPartnerRevenueReport} from '../../../API/PartnerRevenue';
import OutletIcon from '@mui/icons-material/Outlet';
import {toast} from 'react-toastify';
import {format} from 'date-fns';

const PartnerRevenueReport = () => {
    const [data, setData] = useState([]);
    const [dataForCsv, setDataForCsv] = useState([]);
    const [csvHeaders, setCsvHeaders] = useState([]);
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);

    const columns = [
        {id: 'partner', title: 'Partner'},
        {id: 'revenue', title: 'Revenue'},
        {id: 'company', title: 'Company'},
        {id: 'company_type', title: 'Company type'},
        {id: 'irtb_id', title: 'iRTB ID'},
        {id: 'connection', title: 'Connection'},
        {id: 'currency', title: 'Currency'},
        {id: 'partner_code', title: 'Partner code'},
    ];

    useEffect(() => {
        if (data && data.length > 0) {
            let clonedData = [...data];

            if (clonedData.length > 1) {
                const totalRow = clonedData.shift();
                clonedData.push(totalRow);
            }

            const preparedData = clonedData.map((row) => row.value);
            const headersPresent = columns.filter((col) => preparedData.some((item) => Object.prototype.hasOwnProperty.call(item, col.id)));
            setCsvHeaders(headersPresent.map((col) => col.title));

            const processedData = preparedData.map((item) => {
                const newItem = {};
                headersPresent.forEach((header) => {
                    newItem[header.title] = item[header.id] !== undefined ? item[header.id] : '';
                });
                return newItem;
            });

            setDataForCsv(processedData);
        } else {
            setCsvHeaders([]);
            setDataForCsv([]);
        }

        setName(`PartnerRevenueReport - ${format(new Date(), 'yyyy-MM-dd')}`);
    }, [data]);

    const handleSubmit = async (options) => {
        try {
            setLoading(true);
            const response = await fetchPartnerRevenueReport(options);
            if (!response?.data || Object.keys(response.data).length === 0) {
                setData(false);
            } else {
                setData(Object.entries(response.data).map(([key, value]) => ({key, value})));
            }
            setLoading(false);
        } catch (err) {
            toast.error('Oops, there seems to be an error on the server. The technical team has already received information about this. We apologize for the inconvenience and we will let you know when it is fixed.');
            console.error(err);
            setLoading(false);
        }
    };

    return (
        <div>
            <Grid container spacing={2}
                  sx={{
                      padding: 2,
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '4px',
                      paddingLeft: 0,
                      paddingTop: 0,
                      marginLeft: '-10px',
                      marginTop: '-5px',
                      boxShadow: '0 0 10px rgba(0, 0, 0, 0.08)',
                  }}
            >
                <Grid item xs={12} md={12} sx={{width: '100%'}}>
                    <Accordion sx={{width: '100%'}}>
                        <AccordionSummary
                            expandIcon={<InfoOutlinedIcon/>}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                            sx={{
                                backgroundColor: '#f0f3f5',
                                width: '100%',
                                height: '35px !important',
                                minHeight: '25px !important',
                            }}
                        >
                            Explanations
                        </AccordionSummary>
                        <AccordionDetails sx={{width: '100%'}}>
                            <Box>
                                <Typography variant="subtitle1" sx={{marginBottom: '1rem'}}>
                                    Partner API revenue collected into one report.
                                </Typography>
                                <Typography variant="body1" sx={{marginLeft: '1rem'}}>
                                    Date, partner name and revenue are always shown. Use the checkboxes to add company, connection, currency and other fields.
                                </Typography>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Grid>
            </Grid>

            <Grid container spacing={2}
                  sx={{
                      padding: 2,
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '4px',
                      paddingLeft: 0,
                      marginLeft: '-10px',
                      boxShadow: '0 0 10px rgba(0, 0, 0, 0.08)',
                      marginTop: '20px',
                  }}
            >
                <Grid item xs={12} md={12} sx={{width: '100%'}}>
                    <PartnerRevenueOptionsForm onSubmit={handleSubmit}/>
                </Grid>
                <Grid item xs={12} md={12}>
                    {dataForCsv.length > 0 && <DownloadButtonWithHaders
                        data={dataForCsv}
                        headers={csvHeaders}
                        label={name}
                        sx={{float: 'right'}}
                    />}
                </Grid>
                <Grid item xs={12} md={12}>
                    {loading &&
                        <Box justifyContent="center" textAlign="center" alignItems="center">
                            <CircularProgress size={200}/>
                        </Box>
                    }
                    {data === false ? (
                        <Typography variant="h4" component="h2" align="center"><OutletIcon fontSize="large"/> NO DATA </Typography>
                    ) : (
                        <PartnerRevenueDataGrid data={data}/>
                    )}
                </Grid>
            </Grid>
        </div>
    );
};

export default PartnerRevenueReport;
